/**
 * Anthropic Tool Factory for MorphClient
 * 
 * Creates Anthropic-compatible tools that inherit the API key from MorphClient.
 */

import type { MorphClientConfig } from '../client.js';
import { createWarpGrepTool, createGitHubSearchTool } from '../tools/warp_grep/anthropic.js';
import { createCodebaseSearchTool } from '../tools/codebase_search/anthropic.js';
import { createEditFileTool } from '../tools/fastapply/anthropic.js';
import { createExploreSubagent } from '../subagents/anthropic.js';
import type { WarpGrepToolConfig, GitHubSearchToolConfig } from '../tools/warp_grep/types.js';
import type { CodebaseSearchConfig } from '../tools/codebase_search/types.js';
import type { EditFileConfig } from '../tools/fastapply/types.js';
import type { ExploreSubagentConfig } from '../subagents/types.js';

/**
 * Factory for creating Anthropic-compatible tools with inherited API key
 * 
 * @example
 * ```typescript
 * const morph = new MorphClient({ apiKey: process.env.MORPH_API_KEY });
 * 
 * const grepTool = morph.anthropic.createWarpGrepTool({ repoRoot: '.' });
 * const searchTool = morph.anthropic.createCodebaseSearchTool({ repoId: 'my-project' });
 * const editTool = morph.anthropic.createEditFileTool({ baseDir: './src' });
 * 
 * // Use with Anthropic client
 * const response = await anthropic.messages.create({
 *   model: 'claude-sonnet-4-5-20250929',
 *   tools: [grepTool, searchTool, editTool],
 *   messages: [{ role: 'user', content: 'Find and fix the bug' }]
 * });
 * ```
 */
export class AnthropicToolFactory {
  constructor(private config: MorphClientConfig) {}

  /**
   * Create an Anthropic-compatible warp grep tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Anthropic Tool with execute and formatResult methods
   */
  createWarpGrepTool(toolConfig: Omit<WarpGrepToolConfig, 'morphApiKey'>) {
    return createWarpGrepTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });

  }

  /**
   * Create an Anthropic-compatible codebase search tool
   * 
   * @param toolConfig - Tool configuration with repoId (apiKey inherited from MorphClient)
   * @returns Anthropic Tool with execute and formatResult methods
   */
  createCodebaseSearchTool(toolConfig: Omit<CodebaseSearchConfig, 'apiKey'>) {
    return createCodebaseSearchTool({ 
      ...toolConfig, 
      apiKey: this.config.apiKey,
    });
  }

  /**
   * Create an Anthropic-compatible edit file tool
   * 
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Anthropic Tool with execute and formatResult methods
   */
  createEditFileTool(toolConfig: Omit<EditFileConfig, 'morphApiKey'> = {}) {
    return createEditFileTool({ 
      ...toolConfig, 
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create an Anthropic-compatible GitHub search tool
   *
   * @param toolConfig - Tool configuration (morphApiKey inherited from MorphClient)
   * @returns Anthropic Tool with execute and formatResult methods
   */
  createGitHubSearchTool(toolConfig?: Omit<GitHubSearchToolConfig, 'morphApiKey'>) {
    return createGitHubSearchTool({
      ...toolConfig,
      morphApiKey: this.config.apiKey,
    });
  }

  /**
   * Create an Explore subagent that searches the codebase and returns summaries
   *
   * @param config - Subagent configuration including client and model (morphApiKey inherited from MorphClient)
   * @returns Explore subagent with .tool, .run(), and .stream() methods
   */
  createExploreSubagent(config: Omit<ExploreSubagentConfig, 'morphApiKey'> & { client: any; model: string }) {
    return createExploreSubagent({
      ...config,
      morphApiKey: this.config.apiKey,
    });
  }
}

